import { cn } from "@plura/shared/utils";

/**
 * Landing redesign badge / eyebrow heading.
 *
 * Two shapes:
 *   - `color` (default): solid accent fill, square-ish 2px corners.
 *   - `pill`: white fill, 1px dark border, fully rounded.
 *
 * Text is always `text-badge` (Geist 700 · 12/14 · 0.03em) + `capitalize`,
 * dark on every variant. Height is pinned (h-7) like the buttons — Figma's
 * per-instance heights vary, so padding alone isn't reliable.
 */
// Global classes (globals.css `@layer components`) — also usable as plain
// `class="badge badge-salmon"` in raw HTML/CMS.
const colorClass = {
    green: "badge-green",
    salmon: "badge-salmon",
    yellow: "badge-yellow",
    purple: "badge-purple",
    blue: "badge-blue",
    pink: "badge-pink",
} as const;

export type BadgeColor = keyof typeof colorClass;

type BadgeProps = {
    variant?: "color" | "pill";
    /** Fill color — only applies to the `color` variant. */
    color?: BadgeColor;
    className?: string;
    children: React.ReactNode;
};

export function Badge({ variant = "color", color = "salmon", className, children }: BadgeProps) {
    const shape = variant === "pill" ? "badge-pill" : colorClass[color];
    return <span className={cn("badge", shape, className)}>{children}</span>;
}
